import { useTranslation } from '@exam-room/translations';
import { cn, toHHMMSS } from '@exam-room/utils';
import { useDisclosure, useIdle, useInterval, useSetState } from '@shared/hooks';
import { useEffect, useMemo } from 'react';

import { useExamEvent, useServerTime } from '../../providers';
import { AccessTimeRoundedIcon } from '../icons';

const idleTime = 5000;

interface BaseTimerProps {
  size?: 'sm' | 'lg';
  onTimerEnd?: () => void;
}

interface TimerProps extends BaseTimerProps {
  className?: string;
  adjustedEndedAt: Date;
  displayedEndedAt: Date;
  startedAt: Date;
  totalManualExtendedDurationInMs?: number;
}

interface TimeDisplayProps extends BaseTimerProps {
  startedAt: Date;
  adjustedEndedAt: Date;
  displayedEndedAt: Date;
  totalManualExtendedDurationInMs?: number;
}

function getRemainingTime(startedAt: Date, endedAt: Date, timeOffset: number) {
  const now = new Date().getTime() + timeOffset;
  if (now < startedAt.getTime()) {
    return {
      seconds: Math.floor((endedAt.getTime() - startedAt.getTime()) / 1000),
      milliseconds: 0,
      hasStarted: false,
    };
  }
  const timeDiff = endedAt.getTime() - now;
  return timeDiff > 0
    ? { seconds: Math.floor(timeDiff / 1000), milliseconds: timeDiff % 1000, hasStarted: true }
    : { seconds: 0, milliseconds: 0, hasStarted: true };
}

/**
 * Displays the remaining time for an exam.
 * @param startedAt - When the exam started.
 * @param adjustedEndedAt - Actual end time including manual and auto extensions (used for countdown).
 * @param displayedEndedAt - End time shown to the examinee (including auto extensions).
 * @param totalManualExtendedDurationInMs - Total manual extension in ms; shown as a label when non-zero.
 * @param onTimerEnd - Called once when the countdown reaches zero.
 * @param size - Controls font size of the countdown display.
 */
function TimeDisplay({
  startedAt,
  adjustedEndedAt,
  displayedEndedAt,
  totalManualExtendedDurationInMs,
  onTimerEnd,
  size,
}: TimeDisplayProps) {
  const { t } = useTranslation();
  const { timeOffset } = useServerTime();

  const remainingTime = useMemo(
    () => getRemainingTime(startedAt, adjustedEndedAt, timeOffset),
    [adjustedEndedAt, startedAt, timeOffset],
  );

  const [time, setTime] = useSetState(
    (() => {
      const { seconds, milliseconds } = remainingTime;
      return { seconds, milliseconds };
    })(),
  );

  const { start, stop } = useInterval(
    () => setTime(({ seconds: s }) => ({ seconds: s > 0 ? s - 1 : 0 })),
    1000,
  );

  const isIdle = useIdle(idleTime, {
    events: ['visibilitychange', 'focus'],
    initialState: false,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const { seconds, hasStarted } = getRemainingTime(startedAt, adjustedEndedAt, timeOffset);
      if (hasStarted) {
        clearInterval(interval);
        setTime({ seconds });
        start();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [startedAt, adjustedEndedAt, timeOffset]);

  useEffect(() => {
    if (time.seconds === 0) {
      stop();
      onTimerEnd?.();
    }
  }, [onTimerEnd, time]);

  useEffect(() => {
    if (!isIdle) {
      stop();
      const { seconds, hasStarted } = getRemainingTime(startedAt, adjustedEndedAt, timeOffset);
      setTime({ seconds });
      if (hasStarted) start();
    }
  }, [isIdle, startedAt, adjustedEndedAt, timeOffset]);

  return (
    <div
      className={cn(
        'bg-grey-100 flex flex-col items-center gap-0.5 rounded-sm px-2 py-1',
        time.seconds < 600 && 'bg-danger-100',
      )}
    >
      <div className='text-caption'>{t('header.timeRemaining')}</div>
      <div
        className={cn([
          'w-max shrink-0 font-mono font-semibold',
          size === 'lg' ? 'text-7xl' : '',
          time.seconds < 600 && 'text-danger',
          'leading-[1.25]',
        ])}
      >
        {`${toHHMMSS(time.seconds)}${` / ${toHHMMSS((displayedEndedAt.getTime() - startedAt.getTime()) / 1000)}`}`}
      </div>
      {totalManualExtendedDurationInMs ? (
        <div className='text-xs leading-[18px]'>
          {`(${t('header.individualExtendedTimeValue', { minutes: Math.ceil(totalManualExtendedDurationInMs / 60000) })})`}
        </div>
      ) : null}
    </div>
  );
}

export default function Timer({
  className,
  size = 'sm',
  onTimerEnd,
  startedAt,
  adjustedEndedAt,
  displayedEndedAt,
  totalManualExtendedDurationInMs,
}: TimerProps) {
  const [show] = useDisclosure(true);
  return (
    <div className={cn(['flex flex-col items-center justify-center gap-2', className])}>
      {show ? (
        <TimeDisplay
          startedAt={startedAt}
          adjustedEndedAt={adjustedEndedAt}
          displayedEndedAt={displayedEndedAt}
          totalManualExtendedDurationInMs={totalManualExtendedDurationInMs}
          onTimerEnd={onTimerEnd}
          size={size}
        />
      ) : (
        <AccessTimeRoundedIcon className={cn([size === 'sm' ? 'text-[32px]' : 'text-[40px]'])} />
      )}
    </div>
  );
}
